class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._drawerSwiper = null;
    this.hydrated = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      if (evt.target.matches('#CartDrawer-Overlay')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  hydrate() {
    if (this.hydrated) return;
    const template = this.querySelector('#CartDrawer-Template');
    if (template) {
      this.appendChild(template.content.cloneNode(true));
      this.hydrated = true;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  initDrawerSwiper() {
    if (this._drawerSwiper) return;
    const swiperEl = this.querySelector('.drawer_products');
    if (!swiperEl || typeof Swiper === 'undefined') return;
    const slideCount = swiperEl.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length;

    this._drawerSwiper = new Swiper(swiperEl, {
      spaceBetween: 8,
      slidesPerView: 1,
      roundLengths: true,
      loop: slideCount > 1,
      navigation: {
        nextEl: '.drawer-btn-next',
        prevEl: '.drawer-btn-prev',
        addIcons: false
      }
    });
  }

  resetDrawerSwiper() {
    if (this._drawerSwiper) {
      this._drawerSwiper.destroy(true, true);
      this._drawerSwiper = null;
    }
  }

  reinitDrawerSwiper() {
    this.resetDrawerSwiper();
    this.initDrawerSwiper();
  }

  open(triggeredBy) {
    this.hydrate();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const isActive = this.classList.contains('active');

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (typeof klarnaStyle === 'function') klarnaStyle();
    });

    const onOpenEnd = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
      this.initDrawerSwiper();
    };

    if (isActive) {
      onOpenEnd();
    } else {
      this.addEventListener('transitionend', onOpenEnd, { once: true });
    }
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.resetDrawerSwiper();
    this.hydrate();

    // If #CartDrawer still isn't in the live DOM after hydration, force a re-hydrate.
    // This can happen if the element was previously replaced by another update.
    if (!this.querySelector('#CartDrawer')) {
      this.hydrated = false;
      this.hydrate();
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      let sectionElement = section.selector
        ? this.querySelector(section.selector) || document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const sectionHTML = parsedState.sections[section.id];
      if (!sectionHTML) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });
    setTimeout(() => {
      this.open();
      this.initDrawerSwiper();
      if (typeof editQty === 'function') editQty();
    }, 100);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Try direct query first
    let el = doc.querySelector(selector);
    // If not found, check inside every <template> element (Shopify wraps the drawer in one)
    if (!el) {
      for (const tmpl of doc.querySelectorAll('template')) {
        el = tmpl.content.querySelector(selector);
        if (el) break;
      }
    }
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
